import classNames from "classnames";
import { forwardRef } from "react";
import { twMerge } from "tailwind-merge";

// See: https://mui.com/material-ui/react-card/
// https://mui.com/material-ui/api/paper/

const CardColor = {
  neutral0: "neutral0",
  primary200: "primary200",
  primary900: "primary900",
} as const;

const CardRounded = {
  none: "none",
  small: "small",
  medium: "medium",
  large: "large",
} as const;

const CardShadow = {
  none: "none",
  small: "small",
  medium: "medium",
  large: "large",
} as const;

const CardVariant = {
  elevated: "elevated",
  outlined: "outlined",
} as const;

const CardRoundedConfig = {
  [CardRounded.none]: "",
  [CardRounded.small]: "rounded-md",
  [CardRounded.medium]: "rounded-lg",
  [CardRounded.large]: "rounded-xl",
} as const;

const CardColorConfig = {
  [CardColor.neutral0]: {
    root: "bg-white border-vpGray-300",
  },
  [CardColor.primary200]: {
    root: "bg-vpBlue-200 border-vpBlue-200",
  },
  [CardColor.primary900]: {
    root: "bg-vpBlue-900 border-vpBlue-900",
  },
} as const;

const CardShadowConfig = {
  [CardShadow.none]: "",
  [CardShadow.small]: "shadow-sm",
  [CardShadow.medium]: "shadow-md",
  [CardShadow.large]: "shadow-lg",
} as const;

export type CardRootNode = HTMLDivElement;

export interface CardProps extends React.HTMLAttributes<CardRootNode> {
  color?: keyof typeof CardColor;
  disabled?: boolean;
  rounded?: keyof typeof CardRounded;
  shadow?: keyof typeof CardShadow;
  slots?: {
    root?: {
      css?: string;
    };
  };
  variant?: keyof typeof CardVariant;
}

const Card = forwardRef<CardRootNode, CardProps>(
  (
    {
      color = CardColor.neutral0,
      className,
      rounded = CardRounded.medium,
      shadow = CardShadow.small,
      slots,
      variant = CardVariant.elevated,
      ...props
    },
    ref
  ) => {
    return (
      <div
        className={twMerge(
          classNames(
            "overflow-hidden border",
            CardColorConfig[color].root,
            CardRoundedConfig[rounded],
            CardShadowConfig[shadow],
            className,
            slots?.root?.css
          )
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Card.displayName = Card.name;

export default Card;
